import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Loader2, PlayCircle, RefreshCw, ShieldAlert } from "lucide-react";
import { getAuditRuns, runAuditNow } from "@/lib/site-audit.functions";
import type { AuditCheck, AuditRun } from "@/lib/site-audit.types";

export const Route = createFileRoute("/admin/site-audit")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Site audit · Admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: SiteAuditPage,
});

function fmt(iso: string | null) {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

function statusBadge(run: AuditRun) {
  if (run.status === "pass") return <Badge className="bg-emerald-600">Passed</Badge>;
  if (run.status === "running") return <Badge variant="secondary">Running</Badge>;
  if (run.status === "error") return <Badge variant="destructive">Error</Badge>;
  return <Badge variant="destructive">{run.failed_count} issue(s)</Badge>;
}

function CheckList({ items, tone }: { items: AuditCheck[]; tone: "fixed" | "open" }) {
  if (items.length === 0) return null;
  return (
    <ul className="mt-2 space-y-1 text-xs">
      {items.slice(0, 40).map((c, i) => (
        <li key={`${c.id}-${i}`} className={tone === "fixed" ? "text-emerald-700" : "text-destructive"}>
          <strong>{c.label}</strong>
          {c.scope ? ` (${c.scope})` : ""}: {c.message}
        </li>
      ))}
    </ul>
  );
}

function SiteAuditPage() {
  const load = useServerFn(getAuditRuns);
  const run = useServerFn(runAuditNow);
  const [runs, setRuns] = useState<AuditRun[]>([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scope, setScope] = useState("");
  const [open, setOpen] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      setRuns((await load({})) as AuditRun[]);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load audit history");
    } finally {
      setLoading(false);
    }
  }, [load]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const start = async (payload: { auditType: "full" | "targeted" | "source"; scope?: string | null; rerunOf?: string | null }) => {
    setBusy(true);
    setError(null);
    try {
      await run({ data: payload });
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Audit failed to run");
    } finally {
      setBusy(false);
    }
  };

  const latest = runs[0];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl font-bold">Site audit</h1>
        <p className="text-sm text-muted-foreground">
          Automated checks for page health, metadata, freshness labels, advertising claims, licensing scope,
          calculator math, data feeds and the assistant's guardrails. Safe issues are repaired automatically;
          anything involving wording or compliance judgement is flagged for review, never rewritten.
        </p>
      </div>

      {error && (
        <Alert variant="destructive">
          <ShieldAlert className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Run an audit</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap items-center gap-2">
          <Button onClick={() => void start({ auditType: "full" })} disabled={busy}>
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <PlayCircle className="mr-2 h-4 w-4" />}
            Run full audit now
          </Button>
          <Input
            value={scope}
            onChange={(e) => setScope(e.target.value)}
            placeholder="/florida or a data source id"
            className="w-64"
          />
          <Button
            variant="outline"
            disabled={busy || !scope.trim()}
            onClick={() =>
              void start({
                auditType: scope.trim().startsWith("/") ? "targeted" : "source",
                scope: scope.trim(),
              })
            }
          >
            Run targeted audit
          </Button>
          <Button variant="ghost" onClick={() => void refresh()} disabled={loading}>
            <RefreshCw className="mr-2 h-4 w-4" /> Refresh
          </Button>
        </CardContent>
      </Card>

      {latest && latest.unresolved.length > 0 && (
        <Alert variant="destructive">
          <ShieldAlert className="h-4 w-4" />
          <AlertTitle>{latest.unresolved.length} item(s) need review</AlertTitle>
          <AlertDescription>
            <CheckList items={latest.unresolved} tone="open" />
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Recent runs</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : runs.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground">No audits recorded yet — run one above.</div>
          ) : (
            <div className="divide-y">
              {runs.map((r) => (
                <div key={r.id} className="p-4">
                  <div className="flex flex-wrap items-center gap-3">
                    {statusBadge(r)}
                    <span className="font-semibold capitalize">{r.audit_type} audit</span>
                    <span className="text-xs text-muted-foreground">
                      trigger: {r.trigger}
                      {r.scope ? ` · ${r.scope}` : ""} · started {fmt(r.started_at)} · finished {fmt(r.completed_at)}
                    </span>
                    <span className="text-xs">
                      {r.passed_count} passed · {r.failed_count} failed · {r.auto_fixed.length} auto-fixed
                      {r.severity !== "none" ? ` · severity ${r.severity}` : ""}
                    </span>
                    <div className="ml-auto flex gap-2">
                      <Button size="sm" variant="ghost" onClick={() => setOpen(open === r.id ? null : r.id)}>
                        {open === r.id ? "Hide" : "Details"}
                      </Button>
                      {r.status !== "pass" && (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={busy}
                          onClick={() =>
                            void start({ auditType: r.audit_type, scope: r.scope, rerunOf: r.id })
                          }
                        >
                          Re-run
                        </Button>
                      )}
                    </div>
                  </div>
                  {open === r.id && (
                    <div className="mt-3 grid gap-4 md:grid-cols-2">
                      <div>
                        <div className="text-xs font-semibold uppercase text-muted-foreground">Unresolved</div>
                        {r.unresolved.length ? (
                          <CheckList items={r.unresolved} tone="open" />
                        ) : (
                          <p className="mt-2 text-xs text-emerald-700">Nothing outstanding.</p>
                        )}
                      </div>
                      <div>
                        <div className="text-xs font-semibold uppercase text-muted-foreground">Fixed automatically</div>
                        {r.auto_fixed.length ? (
                          <CheckList items={r.auto_fixed} tone="fixed" />
                        ) : (
                          <p className="mt-2 text-xs text-muted-foreground">No automatic repairs needed.</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
